/**
 * NextActionPanel — Dynamic "What to do next" (V107)
 *
 * Rule-based next-action panel. Queries existing API state to
 * determine the most relevant action for the current user.
 * Updates automatically as user progresses.
 *
 * Props:
 *   workspace  — "sme" | "corporate" | "fi" | "states"
 *   data       — optional pre-fetched state data to avoid extra API call
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, RefreshCw, CheckCircle2, AlertTriangle, Lock, Shield,
         DollarSign, TrendingUp, BarChart2, Map, FileText, Target, LayoutDashboard } from "lucide-react";
import client from "@/api/client";

// ── Rule engine ────────────────────────────────────────────────────────────────
function computeNextActions(workspace, data) {
  if (!data) return [];

  const actions = [];

  if (workspace === "sme") {
    const periods     = data.periods || [];
    const total       = data.total || 0;
    const locked      = periods.filter(p => p.is_locked).length;
    const submitted   = periods.filter(p => (p.decision?.outcome_label || "").length > 0).length;
    const hasTarget   = periods.some(p => p.annual_target_tco2e > 0);
    const avgConf     = periods.length > 0
      ? periods.reduce((s, p) => s + (p.decision?.confidence_score || 0), 0) / periods.length
      : 0;

    if (total === 0) {
      actions.push({ priority: 1, label: "Enter your first month of data", desc: "Start tracking your emissions to begin your climate journey.", path: "/sme/monthly/inputs", icon: BarChart2, color: "teal" });
    } else if (locked === 0) {
      actions.push({ priority: 1, label: "Lock your first reporting period", desc: "Locking makes your data immutable and eligible for verification.", path: "/sme/monthly/inputs", icon: Lock, color: "teal" });
    } else if (!periods.some(p => (p.decision || {})._verification?.status === "submitted" || (p.decision || {})._verification?.status === "under_review")) {
      actions.push({ priority: 1, label: "Submit a locked period for verification", desc: "Verification adds institutional credibility to your credentials.", path: "/sme/credentials", icon: Shield, color: "blue" });
    } else {
      actions.push({ priority: 1, label: "Generate your institutional report", desc: "Download your verified credentials for financing applications.", path: "/sme/credentials", icon: FileText, color: "emerald" });
    }

    if (!hasTarget) {
      actions.push({ priority: 2, label: "Set an annual emissions target", desc: "Targets enable trajectory tracking and Portfolio Intelligence scoring.", path: "/sme/monthly/inputs", icon: Target, color: "amber" });
    }
    if (total >= 3 && avgConf < 60) {
      actions.push({ priority: 2, label: "Improve data confidence score", desc: `Current average: ${avgConf.toFixed(0)}/100. Add more inputs to reach 60+ for institutional use.`, path: "/sme/monthly/inputs", icon: AlertTriangle, color: "amber" });
    }
    if (locked >= 3) {
      actions.push({ priority: 3, label: "Check your Financing Readiness", desc: "Your portfolio has enough data for a financing assessment.", path: "/sme/financing-readiness", icon: DollarSign, color: "purple" });
    }
  }

  else if (workspace === "corporate") {
    const periods = data.periods || [];
    const total   = data.total || 0;
    const locked  = periods.filter(p => p.is_locked).length;

    if (total === 0) {
      actions.push({ priority: 1, label: "Enter your first monthly emissions", desc: "Track Scope 1/2/3 emissions including process, energy, and value chain.", path: "/corporate/monthly/inputs", icon: BarChart2, color: "teal" });
    } else if (locked === 0) {
      actions.push({ priority: 1, label: "Lock your first reporting period", desc: "Locked periods enable IFRS S2 disclosures and portfolio intelligence.", path: "/corporate/monthly/inputs", icon: Lock, color: "teal" });
    } else {
      actions.push({ priority: 1, label: "Explore Carbon Monetisation", desc: "Identify fuel switching, efficiency, and renewable opportunities in your operations.", path: "/corporate/monetisation", icon: DollarSign, color: "emerald" });
    }
    if (total >= 3) {
      actions.push({ priority: 2, label: "Review Portfolio Intelligence", desc: "Board-level trajectory and credibility assessment with forward projection.", path: "/corporate/monthly/trends", icon: TrendingUp, color: "blue" });
    }
    actions.push({ priority: 3, label: "Review Climate Credentials", desc: "7-section institutional credentials with full calculation trace.", path: "/corporate/credentials", icon: Shield, color: "purple" });
  }

  else if (workspace === "fi") {
    const deals = data.total_fi_deals || 0;
    const pending = (data.deal_stage_breakdown || {}).screening || 0;

    if (deals === 0) {
      actions.push({ priority: 1, label: "Start with the Executive Dashboard", desc: "See pipeline value, deals requiring decision, signal scores, and capital deployment.", path: "/fi/workspace", icon: LayoutDashboard, color: "teal" });
    } else {
      if (pending > 0) {
        actions.push({ priority: 1, label: `${pending} deal(s) at Screening — review in pipeline`, desc: "Evaluate incoming deals and advance eligible ones to due diligence.", path: "/fi/workspace", icon: ChevronRight, color: "teal" });
      }
      actions.push({ priority: 2, label: "Run eligibility assessment", desc: "Assess climate eligibility across the 4-gate framework.", path: "/fi/workspace", icon: Shield, color: "blue" });
    }
    actions.push({ priority: 3, label: "View portfolio deployment metrics", desc: "Pipeline value, stage breakdown, and capital utilisation.", path: "/fi/workspace", icon: BarChart2, color: "purple" });
  }

  else if (workspace === "states") {
    const progs = data.total || 0;

    if (progs === 0) {
      actions.push({ priority: 1, label: "Create your first state programme", desc: "Structure PoA or CPA programmes aggregating projects from your state.", path: "programmes", icon: Map, color: "teal" });
    } else {
      const noProjectProgs = (data.programmes || []).filter(p => (p.project_count || 0) === 0);
      if (noProjectProgs.length > 0) {
        actions.push({ priority: 1, label: "Add projects to your programme", desc: `${noProjectProgs.length} programme(s) have no projects. Add eligible projects to enable FI submission.`, path: "programmes", icon: Map, color: "amber" });
      }
      const notSubmitted = (data.programmes || []).filter(p => p.status !== "submitted" && (p.project_count || 0) > 0);
      if (notSubmitted.length > 0) {
        actions.push({ priority: 1, label: "Submit programme to FI pipeline", desc: `${notSubmitted.length} programme(s) ready for FI submission.`, path: "programmes", icon: DollarSign, color: "teal" });
      }
    }
    actions.push({ priority: 2, label: "View state climate snapshot", desc: "Aggregated emissions and reduction potential across all organizations.", path: "climate-snapshot", icon: BarChart2, color: "blue" });
  }

  return actions.sort((a, b) => a.priority - b.priority).slice(0, 3);
}


// ── Component ─────────────────────────────────────────────────────────────────
const ICON_COLORS = {
  teal:    "bg-teal-50 text-teal-600",
  blue:    "bg-blue-50 text-blue-600",
  amber:   "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-700",
  purple:  "bg-purple-50 text-purple-600",
  red:     "bg-red-50 text-red-600",
};

export default function NextActionPanel({ workspace, stateId, className = "" }) {
  const navigate = useNavigate();
  const [actions,  setActions]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        let data = null;
        if (workspace === "sme") {
          const r = await client.get("/sme/periodic/periods");
          data = r.data;
        } else if (workspace === "corporate") {
          const r = await client.get("/corporate/periodic/periods");
          data = r.data;
        } else if (workspace === "fi") {
          const r = await client.get("/admin/workspace/pipeline-overview").catch(() => ({ data: {} }));
          data = r.data;
        } else if (workspace === "states" && stateId) {
          const r = await client.get(`/states/programmes?geography=all`).catch(() => ({ data: { programmes: [], total: 0 } }));
          data = r.data;
        }
        setActions(computeNextActions(workspace, data));
      } catch {
        setActions([]);
      }
      setLoading(false);
    };
    fetchData();
  }, [workspace, stateId]);

  if (loading) return null;
  if (actions.length === 0) return null;

  const topAction = actions[0];
  const IconComp  = topAction.icon;

  const handleNavigate = (path) => {
    if (path.startsWith("/")) {
      navigate(path);
    } else if (stateId) {
      navigate(`/states/${stateId}/${path}`);
    } else {
      navigate(`/${workspace}/${path}`);
    }
  };

  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${className}`}
      data-testid="next-action-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">What to do next</p>
        <span className="text-[7px] text-slate-300">Rule-based</span>
      </div>

      {/* Primary action */}
      <button onClick={() => handleNavigate(topAction.path)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 text-left transition-colors">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${ICON_COLORS[topAction.color]}`}>
          <IconComp size={16} />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold text-[#0F172A]">{topAction.label}</p>
          <p className="text-[9px] text-slate-400 mt-0.5 leading-relaxed">{topAction.desc}</p>
        </div>
        <ChevronRight size={14} className="text-slate-300 shrink-0" />
      </button>

      {/* Secondary actions */}
      {actions.slice(1).map((action, i) => {
        const AIcon = action.icon;
        return (
          <button key={i} onClick={() => handleNavigate(action.path)}
            className="w-full flex items-center gap-2.5 px-5 py-2.5 border-t border-slate-50 hover:bg-slate-50/50 text-left">
            <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${ICON_COLORS[action.color]}`}>
              <AIcon size={12} />
            </div>
            <p className="text-[10px] font-medium text-slate-600 flex-1">{action.label}</p>
            <ChevronRight size={11} className="text-slate-300 shrink-0" />
          </button>
        );
      })}
    </div>
  );
}
